import React from "react";
import { CheckCircle, ArrowRight } from "lucide-react";

// Configuration constants
const CONFIG = {
  logo: {
    src: "/logo.svg",
    alt: "MyPurposeProfile",
    className: "w-40 sm:w-48 mx-auto mb-3",
  },
};

// Shared logo component
const Logo = () => (
  <div className="text-center mb-6 animate-fadeIn">
    <img
      src={CONFIG.logo.src}
      alt={CONFIG.logo.alt}
      className={CONFIG.logo.className}
      onError={(e) => (e.target.style.display = "none")}
    />
  </div>
);

const SuccessPage = ({ sessionStatus, purchaseValue, purchaseCurrency }) => {
  const isPaymentComplete = sessionStatus === "complete";
  const isPaymentIncomplete = sessionStatus === "open";

  // Scroll to top on mount
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <div className="min-h-screen gradient-bg flex items-center justify-center py-6 px-4">
      <div className="w-full max-w-xl">
        <div className="glass rounded-2xl p-6 sm:p-8 md:p-10 text-center animate-fadeIn">
          {isPaymentComplete ? (
            <>
              <Logo />

              {/* Success State */}
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
              </div>

              <h1 className="text-2xl sm:text-3xl font-medium text-gray-900 mb-3">
                Payment Successful!
              </h1>

              <p className="text-sm sm:text-base text-gray-600 mb-6 leading-relaxed px-2">
                Thank you for purchasing your Full Purpose Profile. You now have
                access to the complete 25-question assessment.
              </p>

              {/* Payment Details - Mobile optimized */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
                <h3 className="text-sm font-medium text-gray-900 mb-3">
                  Payment Details
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Product:</span>
                    <span className="text-xs font-medium text-gray-900">
                      Full Purpose Profile
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Amount:</span>
                    <span className="text-xs font-medium text-gray-900">
                      {purchaseValue && purchaseCurrency
                        ? `${purchaseCurrency} ${purchaseValue.toFixed(2)}`
                        : "$47.00"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Status:</span>
                    <span className="text-xs font-medium text-green-600">
                      Paid
                    </span>
                  </div>
                </div>
              </div>

              {/* Email Notification - Mobile optimized */}
              <div className="glass-light rounded-xl p-4 border border-purple-200">
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg
                      className="w-4 h-4 text-purple-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      ></path>
                    </svg>
                  </div>
                  <div className="text-left">
                    <h3 className="text-sm font-medium text-gray-900 mb-1">
                      Check Your Email
                    </h3>
                    <p className="text-xs text-gray-700 leading-relaxed">
                      We've sent you an email with a direct link to access your
                      full assessment. Check your inbox (and spam folder) for
                      the access link.
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : isPaymentIncomplete ? (
            <>
              <Logo />

              {/* Incomplete Payment State */}
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-red-600 text-2xl">❌</span>
                </div>
              </div>

              <h1 className="text-2xl sm:text-3xl font-medium text-gray-900 mb-3">
                Payment Incomplete
              </h1>

              <p className="text-sm sm:text-base text-gray-600 mb-6 leading-relaxed px-2">
                Your payment was not completed. Please try again or contact
                support if you have any questions.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="/"
                  className="premium-button text-white px-6 py-3 rounded-xl font-semibold text-sm inline-flex items-center justify-center gap-2 hover-lift transition-all duration-200"
                >
                  Try Payment Again
                  <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href="/"
                  className="bg-gray-100 text-gray-700 px-6 py-3 rounded-xl font-semibold text-sm inline-flex items-center justify-center hover:bg-gray-200 transition-all duration-200"
                >
                  Back to Assessment
                </a>
              </div>
            </>
          ) : (
            <>
              <Logo />

              {/* Default Success State */}
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
              </div>

              <h1 className="text-2xl sm:text-3xl font-medium text-gray-900 mb-3">
                Payment Successful!
              </h1>

              <p className="text-sm sm:text-base text-gray-600 mb-6 leading-relaxed px-2">
                Thank you for purchasing your Full Purpose Profile. You now have
                access to the complete 25-question assessment.
              </p>

              {/* Payment Details - Mobile optimized */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
                <h3 className="text-sm font-medium text-gray-900 mb-3">
                  Payment Details
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Product:</span>
                    <span className="text-xs font-medium text-gray-900">
                      Full Purpose Profile
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Amount:</span>
                    <span className="text-xs font-medium text-gray-900">
                      {purchaseValue && purchaseCurrency
                        ? `${purchaseCurrency} ${purchaseValue.toFixed(2)}`
                        : "$47.00"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Status:</span>
                    <span className="text-xs font-medium text-green-600">
                      Paid
                    </span>
                  </div>
                </div>
              </div>

              {/* Email Notification - Mobile optimized */}
              <div className="glass-light rounded-xl p-4 border border-purple-200">
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg
                      className="w-4 h-4 text-purple-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      ></path>
                    </svg>
                  </div>
                  <div className="text-left">
                    <h3 className="text-sm font-medium text-gray-900 mb-1">
                      Check Your Email
                    </h3>
                    <p className="text-xs text-gray-700 leading-relaxed">
                      We've sent you an email with a direct link to access your
                      full assessment. Check your inbox (and spam folder) for
                      the access link.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;
